// Dayjs
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
dayjs.extend(isoWeek)

// Classnames
import classNames from 'classnames'

// Groupby
import groupBy from 'lodash/groupBy'

// OrderBy
import orderBy from 'lodash/orderBy'

// API
import useApiSWR from '@/hooks/useApiSWR'

// Interval packer
import intervalPacker from 'greedy-interval-packer'

// Mantine
import { Button } from '@mantine/core'

// UI
import ScrollableScreen from '../ui/ScrollableScreen'

export default function ProductionOrderCalendar() {
    // Load production orders
    const { data: productionOrders = [] } = useApiSWR(
        '/production-orders/calendar'
    )

    const firstDay = dayjs('2024-12-30')

    const days = []

    for (let i = 0; i < 365; i++) {
        const day = firstDay.add(i, 'day')

        days.push({
            date: day.format('D/MM'),
            day_of_month: day.format('DD'),
            week: day.isoWeek(),
            week_day: day.isoWeekday(),
            week_year: day.isoWeekYear(),
            week_key: day.isoWeek() + '-' + day.isoWeekYear(),
            week_name:
                'S' +
                day.isoWeek() +
                ' ' +
                day.isoWeekYear() +
                ' - ' +
                day.format('MMM'),
            is_weekend: day.isoWeekday() === 6 || day.isoWeekday() === 7,
            is_today: day.isSame(dayjs(), 'day'),
            is_same_weekyear: day.isoWeekYear() === dayjs().isoWeekYear(),
        })
    }

    const daysByWeek = Object.entries(groupBy(days, 'week_key')).map(
        ([week_key, days]) => ({
            week: {
                number: week_key,
                name: days[0].week_name,
                year: days[0].week_year,
            },
            days,
        })
    )

    // Interval packer

    const intervals = productionOrders.map((o) => {
        // If ending_at is before today, set it to today
        const ending_at =
            dayjs(o.ending_at).isBefore(dayjs()) && o.status === 'open'
                ? dayjs().add(1, 'day')
                : dayjs(o.ending_at)

        o.ending_at = ending_at

        const start = dayjs(o.starting_at).unix()
        const end = ending_at.add(1, 'day').unix()

        const daysSinceFirstDay = dayjs(o.starting_at).diff(firstDay, 'day')
        const duration = dayjs(o.ending_at).diff(o.starting_at, 'day')

        const left = daysSinceFirstDay * 35
        const width = duration * 35

        return {
            start,
            end,
            left,
            width,
            ...o,
        }
    })

    const rows = intervalPacker(intervals)

    const breadcrumbs = [
        { label: 'Assemblage', href: '/assembly' },
        { label: 'Calendrier', href: '/assembly/calendar' },
    ]

    return (
        <ScrollableScreen breadcrumbs={breadcrumbs}>
            <div className="overscroll-contain overflow-hidden p-5 h-full bg-gray-50">
                {/* La grille */}
                <div className="h-full overflow-x-auto overscroll-contain border bg-white rounded-xl">
                    <div className="w-max flex border-b bg-white sticky top-0 z-10 bg-white">
                        {daysByWeek.map((week) => (
                            <div
                                key={week.week.number}
                                className="shrink-0  even:bg-gray-50 bg-white"
                            >
                                <div className="text-sm text-gray-500 border-b">
                                    <span className="sticky left-0 p-2 inline-block">
                                        {week.week.name}
                                    </span>
                                </div>

                                <div className="flex bg-white">
                                    {week.days.map((day) => (
                                        <div
                                            key={day.date}
                                            className="shrink-0 text-xs p-2 text-center text-gray-600 w-[35px] box-border border-r truncate"
                                        >
                                            <div
                                                className={classNames(
                                                    'truncate shrink-0',
                                                    {
                                                        'text-gray-400':
                                                            day.is_weekend,
                                                        'text-red-500 font-bold':
                                                            day.is_today,
                                                    }
                                                )}
                                            >
                                                {day.day_of_month}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="flex flex-col gap-2 py-2 z-0 relative">
                        {rows.map((row) => (
                            <div
                                key={row.id}
                                className="flex gap-5 relative h-[50px] z-10"
                            >
                                {row.map((po) => (
                                    <div
                                        key={po.id}
                                        className="shrink-0 absolute"
                                        style={{
                                            left: po.left,
                                            width: po.width,
                                        }}
                                    >
                                        <div
                                            className={classNames(
                                                'border bg-white rounded-xl p-1 px-2 text-sm h-[50px] flex flex-col cursor-pointer hover:bg-gray-50',
                                                {
                                                    'bg-green-100 text-green-500 hover:bg-green-200':
                                                        po.status === 'open',
                                                }
                                            )}
                                        >
                                            <div className="">
                                                <span className="sticky inline-block px-1 left-2 truncate">
                                                    <span className="block">
                                                        A{po.id}
                                                        {' - '}
                                                        {po.component.name}
                                                    </span>

                                                    <span className="text-xs text-gray-500 block truncate">
                                                        {po.location.name}
                                                    </span>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ))}

                        {Array.from({ length: 52 }).map((_, index) => (
                            <div
                                key={index}
                                className="even:bg-gray-100 absolute top-0 bottom-0 z-0"
                                style={{
                                    left: index * 35 * 7,
                                    width: 35 * 7,
                                }}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </ScrollableScreen>
    )
}
